import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { ClinicalMoment } from "@/domain/clinicalMomentEngine";

const kindLabel: Record<string, string> = {
  farmacocinetica: "Interação PK",
  farmacodinamica: "Interação PD",
  aditiva: "Adição de efeitos",
  sobreposicao: "Sobreposição temporal",
  "carry-over": "Carry-over residual",
  "baixa-relevancia": "Baixa relevância agora",
  "clinicamente-importante": "Clinicamente importante",
};

const tierLabel = { dominante: "dominante", ativa: "ativa", baixa: "baixa", residual: "residual" } as const;

export function ClinicalMomentPanel({ moment }: { moment: ClinicalMoment }) {
  const d = moment.dominant;
  return (
    <section className="space-y-3 min-w-0">
      <div className="px-1">
        <h2 className="text-lg font-semibold">Momento atual</h2>
        <p className="text-xs text-muted-foreground">Fotografia temporal ponderada pela exposição estimada agora. Intensidade relativa do modelo, não concentração.</p>
      </div>

      <Card className="rounded-3xl p-4 min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Fenômeno dominante</div>
        {d ? (
          <>
            <div className="mt-1 flex flex-wrap items-center gap-2"><span className="font-semibold">{d.label}</span><Badge variant="outline">{d.level}</Badge></div>
            <p className="mt-1 text-xs text-muted-foreground">Esperado: {d.physiology}. Contribuintes: {d.main.map((m) => `${m.name} (${m.share}%${m.residualOnly ? ", residual" : ""})`).join(" · ")}.</p>
            {d.objective ? <p className="mt-1 text-xs">{d.objective}</p> : null}
          </>
        ) : <p className="mt-1 text-sm text-muted-foreground">Nenhuma exposição com efeito estimável neste momento.</p>}
      </Card>

      <div className="grid gap-3 lg:grid-cols-2 min-w-0">
        <Card className="rounded-3xl p-4 min-w-0">
          <div className="text-sm font-semibold">Exposições</div>
          <div className="mt-2 space-y-2">
            {moment.exposures.map((e) => (
              <div key={e.key + e.source} className="min-w-0">
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate">{e.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{e.phase} · {tierLabel[e.tier]}</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"><div className={`h-full rounded-full ${e.source === "registrada" ? "bg-primary" : "bg-muted-foreground/50"}`} style={{ width: `${Math.max(2, e.intensity)}%` }} /></div>
                {e.note ? <div className="mt-0.5 text-[11px] text-muted-foreground">{e.note}</div> : null}
              </div>
            ))}
            {!moment.exposures.length ? <div className="text-xs text-muted-foreground">Sem exposições.</div> : null}
          </div>
        </Card>

        <Card className="rounded-3xl p-4 min-w-0">
          <div className="text-sm font-semibold">Eixos clínicos agregados</div>
          <div className="mt-2 space-y-3">
            {moment.axes.map((a) => (
              <div key={a.id}>
                <div className="flex items-center justify-between gap-2 text-sm"><span>{a.label}</span><Badge variant="outline" className="text-[10px]">{a.level}</Badge></div>
                <div className="text-[11px] text-muted-foreground">{a.main.map((m) => `${m.name}${m.residualOnly ? " (residual)" : ""}`).join(" · ")}{a.changesInterpretation ? "" : " — não muda a interpretação atual"}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {moment.interactions.length ? (
        <Card className="rounded-3xl p-4 min-w-0">
          <div className="text-sm font-semibold">Interações relevantes agora</div>
          <div className="mt-2 space-y-2">
            {moment.interactions.map((i) => (
              <div key={i.id} className="rounded-2xl border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2"><span className="text-sm font-medium">{i.title}</span><Badge variant="outline" className="text-[10px]">{kindLabel[i.kind]}</Badge></div>
                <div className="mt-1 text-xs text-muted-foreground">{i.rationale}</div>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {moment.lowRelevance.length ? (
        <details className="rounded-3xl border p-4 text-sm">
          <summary className="cursor-pointer font-medium">{moment.lowRelevance.length} sinal(is) de baixa relevância/residual</summary>
          <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {moment.lowRelevance.map((i) => <li key={i.id}>{i.title} — {kindLabel[i.kind]} (exposição ~{i.magnitude}/100)</li>)}
          </ul>
        </details>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-3 min-w-0">
        <Card className="rounded-3xl p-4"><div className="text-xs font-semibold">Modificadores</div><ul className="mt-1 space-y-1 text-[11px] text-muted-foreground">{moment.modifiers.length ? moment.modifiers.map((m) => <li key={m}>{m}</li>) : <li>Nenhum registrado.</li>}</ul></Card>
        <Card className="rounded-3xl p-4"><div className="text-xs font-semibold">Incerto</div><ul className="mt-1 space-y-1 text-[11px] text-muted-foreground">{moment.uncertain.length ? moment.uncertain.map((m) => <li key={m}>{m}</li>) : <li>—</li>}</ul></Card>
        <Card className="rounded-3xl p-4"><div className="text-xs font-semibold">Mudaria a conclusão</div><ul className="mt-1 space-y-1 text-[11px] text-muted-foreground">{moment.wouldChange.length ? moment.wouldChange.map((m) => <li key={m}>{m}</li>) : <li>—</li>}</ul></Card>
      </div>
    </section>
  );
}
